import { Group, TextInput } from '@mantine/core';
import { Search } from 'lucide-react';
import { Pill } from './Pill';

export interface CatalogChip<T extends string> {
  key: T;
  label: string;
}

export interface CatalogFiltersProps<T extends string> {
  search: string;
  onSearchChange: (next: string) => void;
  searchPlaceholder?: string;
  /** Accessible label for the search box — say what is being filtered. */
  searchLabel: string;
  chips?: ReadonlyArray<CatalogChip<T>>;
  active?: ReadonlySet<T>;
  onToggle?: (key: T) => void;
}

/**
 * CatalogFilters — the shared "free-text search + toggleable chips" block.
 *
 * Extracted from scenarios.index (the only catalog that had it) so labs and any
 * future catalog stay consistent instead of each route hand-rolling its own
 * filter bar. Search state stays with the caller so it can be deferred with
 * `useDeferredValue`.
 */
export function CatalogFilters<T extends string>({
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  searchLabel,
  chips,
  active,
  onToggle,
}: CatalogFiltersProps<T>) {
  return (
    <Group justify="space-between" wrap="wrap" gap="md">
      <TextInput
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearchChange(e.currentTarget.value)}
        leftSection={<Search size={14} />}
        style={{ minWidth: 320, flex: 1, maxWidth: 480 }}
        aria-label={searchLabel}
      />
      {chips && chips.length > 0 && onToggle ? (
        <Group gap={6}>
          {chips.map((c) => {
            const on = active?.has(c.key) ?? false;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onToggle(c.key)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
                aria-pressed={on}
                aria-label={`${on ? 'Remove' : 'Add'} ${c.label} filter`}
              >
                <Pill variant={on ? 'success' : 'neutral'} size="sm">
                  {c.label}
                </Pill>
              </button>
            );
          })}
        </Group>
      ) : null}
    </Group>
  );
}
